import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const LenisContext = createContext<Lenis | null>(null);

/** Dostęp do instancji Lenis np. do programatycznego scrollTo w linkach nawigacji. */
export function useLenis(): Lenis | null {
  return useContext(LenisContext);
}

type LenisProviderProps = {
  children: ReactNode;
};

/**
 * Owija całą aplikację. Jeśli użytkownik ma włączone "ogranicz ruch",
 * Lenis w ogóle się nie inicjalizuje — strona wraca do natywnego,
 * natychmiastowego scrolla przeglądarki.
 *
 * Parametry (lerp 0.1, duration 1.15) dają odczuwalną, ale nie "gumową"
 * bezwładność — dopasowaną do referencji: Linear / Vercel / Framer.
 */
export function LenisProvider({ children }: LenisProviderProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [lenis, setLenis] = useState<Lenis | null>(null);
  const rafId = useRef<number>();

  useEffect(() => {
    // Touch-only devices (phones/tablets): native iOS/Android scroll is already
    // smooth, and Lenis v1 passive:false touch listeners can block
    // IntersectionObserver on iOS Safari, causing all reveal animations to freeze.
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (reducedMotion || isTouch) {
      setLenis(null);
      return;
    }

    const instance = new Lenis({
      duration: 1.15,
      lerp: 0.1,
      easing: (t: number) => 1 - Math.pow(1 - t, 3),
      smoothWheel: true,
      wheelMultiplier: 1,
      // Modals, mobile menu, tables and CRM panels keep their own native scroll.
      allowNestedScroll: true,
      prevent: (node: HTMLElement) => !!node.closest?.("[role=dialog], [data-radix-scroll-area-viewport], [data-lenis-prevent]"),
    });

    setLenis(instance);

    function raf(time: number) {
      instance.raf(time);
      rafId.current = requestAnimationFrame(raf);
    }
    rafId.current = requestAnimationFrame(raf);

    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      instance.destroy();
      setLenis(null);
    };
  }, [reducedMotion]);

  return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>;
}
